import React, { useState, useEffect } from 'react'; // added useEffect
import SearchModal from './SearchModal';
import { Box, Stack, Button } from '@mui/material';
import { ApiClient } from '../services/apiClient'; // added import
import { logger } from '../logger';

const Sidebar = ({ startNewConversation, clearChat, collapsed, onToggleCollapse, project, userEmail, onSelectConversation }) => {
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [convError, setConvError] = useState(null);

  const formatRelative = (t) => {
    const diff = Date.now() - t;
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  };

  useEffect(() => {
    let active = true;
    async function load() {
      if (!project || !userEmail) return;
      setConvError(null);
      try {
        const normProject = typeof project === 'object'
          ? (project.id || project.project || project.code || project.name || project)
          : project;
        const res = await ApiClient.getConversations({
          project: normProject,
          user: userEmail.toLowerCase()
        });
        const list = Array.isArray(res?.conversations) ? res.conversations : [];
        if (active) setConversations(list);
        if (active && list.length === 0) {
          logger.warn('[Sidebar] No conversations for', normProject, userEmail);
        }
      } catch (e) {
        if (active) setConvError('Failed to load');
      }
    }
    load();
    return () => { active = false; };
  }, [project, userEmail]);

  return (
    <>
      <Box
        className="sidebar"
        sx={{
          width: { xs: '100%', md: collapsed ? '60px' : 'clamp(220px, 20vw, 280px)' },
          bgcolor: '#fff',
          borderRight: '1px solid #e5e5e5',
          p: { xs: 2, md: collapsed ? 1 : 2.5 },
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.3s ease',
        }}
      >
        {/* Expanded state action buttons */}
        {!collapsed && (
          <Box className="sidebar-header" sx={{ mb: 2 }}>
            <Stack spacing={1.5}>
              <Button
                className="start-conversation-btn"
                onClick={startNewConversation}
                variant="outlined"
                sx={{
                  justifyContent: 'flex-start',
                  gap: 1.25,
                  borderColor: '#edd4d6',
                  bgcolor: '#f9ebec',
                  color: 'primary.main',
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontSize: '12px', // decreased from previous larger size
                  fontWeight: 600,
                  '&:hover': { bgcolor: '#f3dadc', borderColor: '#edd4d6' },
                  py: 1.5, px: 1.75,
                }}
                startIcon={<Box component="img" src="/images/start-icon.svg" alt="Start" sx={{ width: 16, height: 16 }} />}
              >
                Start New Conversation
              </Button>

              <Button
                className="search-conversation-btn-left"
                onClick={() => setShowSearchModal(true)}
                variant="outlined"
                sx={{
                  justifyContent: 'flex-start',
                  gap: 1.25,
                  borderColor: '#e5e5e5',
                  bgcolor: '#ffffff',
                  color: '#333',
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontSize: '12px', // decreased from previous larger size
                  fontWeight: 600,
                  '&:hover': { bgcolor: '#f5f5f5', borderColor: '#e5e5e5' },
                  py: 1.5, px: 1.75,
                }}
                startIcon={<Box component="img" src="/images/search.svg" alt="Search" sx={{ width: 16, height: 16 }} />}
              >
                Search Conversation
              </Button>
            </Stack>
          </Box>
        )}

        {/* Collapsed state navigation (minimal) */}
        {collapsed && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              mt: 1
            }}
          >
            <Button
              variant="text"
              sx={{
                minWidth: 0,
                flexDirection: 'column',
                p: 0,
                color: '#333',
                fontSize: 11,
                textTransform: 'none',
                gap: 0.5,
                '&:hover': { bgcolor: 'transparent', color: '#000' }
              }}
            >
              <Box component="img" src="/images/add-icon.svg" alt="Home" sx={{ width: 20, height: 20 }} />
              Home
            </Button>
            <Button
              variant="text"
              onClick={() => setShowSearchModal(true)}
              sx={{
                minWidth: 0,
                flexDirection: 'column',
                p: 0,
                color: '#333',
                fontSize: 11,
                textTransform: 'none',
                gap: 0.5,
                '&:hover': { bgcolor: 'transparent', color: '#000' }
              }}
            >
              <Box component="img" src="/images/search-icon.svg" alt="Search" sx={{ width: 20, height: 20 }} />
              Search
            </Button>
            <Button
              variant="text"
              onClick={startNewConversation}
              sx={{
                minWidth: 0,
                flexDirection: 'column',
                p: 0,
                color: '#333',
                fontSize: 11,
                textTransform: 'none',
                gap: 0.5,
                '&:hover': { bgcolor: 'transparent', color: '#000' }
              }}
            >
              <Box component="img" src="/images/ic-user-group.svg" alt="Chats" sx={{ width: 20, height: 20 }} />
              Chats
            </Button>
          </Box>
        )}

        {/* Recent chats (only expanded) */}
        {!collapsed && (
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.5,
            '&::-webkit-scrollbar': { width: 0, height: 0 },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}>
            <div className="recent-chats">
              <div className="recent-chats-title">RECENT CHATS</div>
              {convError && <div style={{ fontSize:12, color:'#d73527', marginTop:4 }}>{convError}</div>}
              {(!convError && conversations.length === 0) && (
                <div style={{ fontSize:12, color:'#888', marginTop:4 }}>No conversations.</div>
              )}
              {conversations.map(c => (
                <div
                  key={c.fetchId || c.id}
                  className="chat-item"
                  onClick={() => onSelectConversation?.(c.fetchId || c.id)}
                  style={{ cursor:'pointer' }}
                >
                  <div className="chat-line">
                    <span className="chat-summary" title={c.title}>{c.title}</span>
                    <span className="chat-time">
                      {c.timestamp ? formatRelative(new Date(c.timestamp).getTime()) : ''}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Box>
        )}

        {/* Condense Nav button fixed at bottom */}
        <Box
          sx={{
            mt: 'auto',
            pt: 2,
            borderTop: collapsed ? 'none' : '1px solid #f0f0f0',
            mb: collapsed ? '10px' : 0,          // lift arrow ~10px when collapsed
            display: 'flex',
            justifyContent: collapsed ? 'center' : 'flex-start'
          }}
        >
          <Button
            onClick={onToggleCollapse}
            sx={{
              width: collapsed ? 'auto' : '100%',
              border: 'none',
              color: '#666',
              fontSize: 12,
              textTransform: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: collapsed ? 'center' : 'flex-start',
              gap: '6px',
              minHeight: 40,
              p: 0,
              '&:hover': { bgcolor: 'transparent', color: '#333' },
            }}
          >
            <Box
              component="img"
              src="/images/icons-solid-arrow.svg"
              alt={collapsed ? 'Expand navigation' : 'Condense navigation'}
              sx={{
                width: 14,
                height: 14,
                transition: 'transform .3s',
                transform: collapsed ? 'rotate(180deg)' : 'none'
              }}
            />
            {!collapsed && <span>Condense Nav</span>}
          </Button>
        </Box>
      </Box>

      {showSearchModal && (
        <SearchModal
          onClose={() => setShowSearchModal(false)}
          project={project}
          userEmail={userEmail}
          onSelectConversation={onSelectConversation}
        />
      )}
    </>
  );
};

export default Sidebar;
